import { useEffect, useRef } from 'react';

/**
 * 登录页左栏底层那张缓慢起伏的点阵(three.js)。
 *
 * ── 为什么按需加载 ────────────────────────────────────────────
 *
 * three 压缩后 150KB 上下,而登录页之外没有任何地方用它。静态 import 会把它
 * 塞进入口包,七个业务页面都要替它买单 —— H1 把入口包从 1142 压到 305KB,
 * 不能在这一页还回去。所以 `import('three')` 放在 effect 里,只有这个组件
 * 真挂上了才拉,拉不到也只是没有背景。
 *
 * ── 什么时候不画 ─────────────────────────────────────────────
 *
 *   · 窄屏(< 1024,也就是登录页单列那档):手机不跑 WebGL
 *   · 没有 WebGL(happy-dom、老浏览器、被禁的 GPU):静默跳过
 *   · `prefers-reduced-motion`:画一帧静止的,不起动画
 *
 * 任何一种情况下页面其它部分都不受影响 —— 这是背景,不是内容。
 */
export function WaveField({ className }: { className?: string }): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    if (!('WebGLRenderingContext' in window)) return;

    let disposed = false;
    let raf = 0;
    let cleanup: (() => void) | null = null;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    void import('three')
      .then((THREE) => {
        if (disposed) return;
        let renderer: InstanceType<typeof THREE.WebGLRenderer>;
        try {
          renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
        } catch {
          return; // 拿不到 context:没有背景而已
        }
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        const scene = new THREE.Scene();
        const cam = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
        cam.position.set(0, 2.2, 7.5);
        cam.lookAt(0, 0, 0);

        // 90×50 的点阵,三重正弦叠加做起伏;每隔 6 行一条横向连线
        const W = 90;
        const H = 50;
        const SP = 0.22;
        const N = W * H;
        const pos = new Float32Array(N * 3);
        let k = 0;
        for (let y = 0; y < H; y += 1) {
          for (let x = 0; x < W; x += 1) {
            pos[k++] = (x - W / 2) * SP;
            pos[k++] = 0;
            pos[k++] = (y - H / 2) * SP;
          }
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
        const mat = new THREE.PointsMaterial({
          color: 0x6f95ff,
          size: 0.03,
          transparent: true,
          opacity: 0.42,
          depthWrite: false,
        });
        const pts = new THREE.Points(geo, mat);
        pts.position.set(-1.2, -1.9, 0);
        pts.rotation.x = -0.05;
        scene.add(pts);

        const lp: number[] = [];
        for (let yy = 0; yy < H; yy += 6) {
          for (let xx = 0; xx < W - 1; xx += 1) {
            const i0 = yy * W + xx;
            const i1 = i0 + 1;
            lp.push(pos[i0 * 3]!, 0, pos[i0 * 3 + 2]!, pos[i1 * 3]!, 0, pos[i1 * 3 + 2]!);
          }
        }
        const lg = new THREE.BufferGeometry();
        lg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lp), 3));
        const lmat = new THREE.LineBasicMaterial({
          color: 0x3b5fd6,
          transparent: true,
          opacity: 0.11,
        });
        const lines = new THREE.LineSegments(lg, lmat);
        lines.position.copy(pts.position);
        lines.rotation.copy(pts.rotation);
        scene.add(lines);

        const resize = (): void => {
          const w = canvas.clientWidth;
          const h = canvas.clientHeight;
          if (w === 0 || h === 0) return;
          renderer.setSize(w, h, false);
          cam.aspect = w / h;
          cam.updateProjectionMatrix();
        };
        resize();
        window.addEventListener('resize', resize);

        const wave = (x: number, z: number, t: number): number =>
          Math.sin(x * 0.9 + t * 1.6) * 0.12 +
          Math.cos(z * 1.3 + t) * 0.1 +
          Math.sin((x + z) * 0.5 + t * 0.7) * 0.08;

        let t = 0;
        const pa = geo.attributes['position'] as InstanceType<typeof THREE.BufferAttribute>;
        const la = lg.attributes['position'] as InstanceType<typeof THREE.BufferAttribute>;
        const frame = (): void => {
          if (disposed) return;
          t += reduce ? 0 : 0.006;
          const arr = pa.array as Float32Array;
          for (let i = 0; i < N; i += 1) arr[i * 3 + 1] = wave(arr[i * 3]!, arr[i * 3 + 2]!, t);
          pa.needsUpdate = true;
          const l = la.array as Float32Array;
          for (let q = 0; q < l.length; q += 3) l[q + 1] = wave(l[q]!, l[q + 2]!, t);
          la.needsUpdate = true;
          renderer.render(scene, cam);
          if (!reduce) raf = window.requestAnimationFrame(frame);
        };
        frame();

        cleanup = () => {
          window.removeEventListener('resize', resize);
          geo.dispose();
          lg.dispose();
          mat.dispose();
          lmat.dispose();
          renderer.dispose();
        };
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      if (raf) window.cancelAnimationFrame(raf);
      cleanup?.();
    };
  }, []);

  return <canvas ref={ref} className={className} aria-hidden data-testid="portal-login-wave" />;
}
